import { Injectable } from "@angular/core";
import { Translation, TranslocoLoader } from "@jsverse/transloco";

@Injectable({ providedIn: "root" })
export class TranslocoRootLoader implements TranslocoLoader {
    public async getTranslation(path: string): Promise<Translation> {
        // `path` is the bare lang for the root namespace (e.g. "en") or a scope-prefixed
        // path for a feature scope (e.g. "about/en"). Feature scopes declare only
        // their path via provideTranslationScope(); resolving the file lives here so
        // the pseudo rule and folder convention are defined in one place.
        //
        // `pseudo` has no files of its own; it serves the English content, which the
        // pseudo interceptor then mangles. See transloco-pseudo.interceptor.ts.
        // The last segment is the language; anything before it is the scope folder. The
        // root namespace (common/validation) has no scope prefix and lives in "shared".
        // JSON lives at "<folder>/i18n/<lang>.json".
        const segments = path.replace(/pseudo$/, "en").split("/");
        const lang = segments.pop();
        const parts = segments.length ? segments : ["shared"];

        // On a failed chunk load (stale deploy, offline, missing file), return an empty
        // translation instead of letting the error reach TranslocoService. That keeps
        // callers that `await load()` (e.g. dashboard-manager.component.ts) running instead
        // of blocking the whole component. Keys in the failed scope then render as raw keys
        // through ReportingMissingHandler, which already reports each one to Sentry.
        try {
            const module = await this.importScope(parts, lang);
            return module.default;
        } catch (error) {
            console.error(`transloco-loader: failed to load "${path}"`, error);
            return {};
        }
    }

    // Co-located shared scopes nest arbitrarily deep (e.g.
    // "shared/components/cerrix-loader"), so the folder can span several path segments.
    // esbuild bundles a template-literal dynamic import by turning each ${…} into a "*"
    // wildcard that does NOT cross "/", so a single `./${folder}/i18n/${lang}.json`
    // import only globs one directory level (./*/i18n/*.json) and never matches a nested
    // scope. We therefore emit one literal import per depth: each is a distinct glob
    // (./*/i18n/*.json, ./*/*/i18n/*.json, …) that esbuild can expand into its own set of
    // lazy chunks. Keep these as literal-prefixed template imports (not import(variable)):
    // esbuild only bundles dynamic import paths that start with a static "./" or "../".
    // This file lives in src/app/shared/i18n, so "../../" is src/app, the scope paths' root.
    // refs: https://esbuild.github.io/api/ (glob-style dynamic imports; "*" excludes "/")
    private importScope(p: string[], lang: string | undefined): Promise<{ default: Translation }> {
        switch (p.length) {
            case 1:
                return import(`../../${p[0]}/i18n/${lang}.json`);
            case 2:
                return import(`../../${p[0]}/${p[1]}/i18n/${lang}.json`);
            case 3:
                return import(`../../${p[0]}/${p[1]}/${p[2]}/i18n/${lang}.json`);
            case 4:
                return import(`../../${p[0]}/${p[1]}/${p[2]}/${p[3]}/i18n/${lang}.json`);
            case 5:
                return import(`../../${p[0]}/${p[1]}/${p[2]}/${p[3]}/${p[4]}/i18n/${lang}.json`);
            default:
                throw new Error(
                    `transloco-loader: unsupported i18n scope depth (${p.length}): ${p.join("/")}. ` +
                        "Add a deeper case to importScope()."
                );
        }
    }
}
